import { useAuth } from '../providers/AuthProvider';
import { useIdentity } from '../providers/IdentityProvider';
import { roleLabels } from '../types/auth';
import { Dropdown, DropdownItem, ChevronDownIcon, LogoutIcon } from '../ui';
import { initialsOf } from './initials';

// Who is signed in, in the top bar: an initials avatar with the name and role beside it, opening
// a menu that holds the account actions (Slice P0-H D1/D3).
//
// Before this slice the top bar ended in four controls of equal visual weight — switcher, name
// block, theme, sign-out — with nothing saying which was the account and which was a setting.
// Folding the identity and its one action into a single menu leaves three ranks: company, then
// user, then theme.
//
// The name resolves display_name -> email, so something identifying is on screen from the first
// paint — the session email is available immediately, while the backend identity (display_name +
// roles) arrives a moment later. No fixed height anywhere: min-h only, so a wrapped Georgian role
// label grows the control instead of being clipped.

export function CurrentUser(): JSX.Element | null {
  const { authUser, signOut } = useAuth();
  const { identity } = useIdentity();

  if (authUser === null) {
    return null;
  }

  const name = identity?.displayName ?? authUser.email;
  const roles = roleLabels(identity?.roles ?? []).join(' · ');

  // No name and no roles yet — render nothing rather than an empty placeholder box.
  if (name === null && roles === '') {
    return null;
  }

  const initials = name === null ? '' : initialsOf(name);

  return (
    <Dropdown
      align="end"
      triggerLabel="მომხმარებლის მენიუ"
      trigger={
        <span className="flex min-h-11 min-w-0 items-center gap-2 rounded-token px-1 py-1 transition-colors hover:bg-surface-muted sm:px-2">
          {/* Initials avatar — the identity anchor that survives 390px, where the text beside it
              is dropped. Decorative: the trigger's aria-label is its accessible name. */}
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
          >
            {initials}
          </span>
          {/* Hidden below 480px — at that width the company switcher and the theme toggle need
              the room more than a name the menu itself repeats. */}
          <span className="hidden min-w-0 flex-col items-start leading-tight min-[480px]:flex">
            {name !== null && (
              <span className="w-full max-w-[9rem] truncate text-sm font-medium text-fg xl:max-w-[14rem]" title={name}>
                {name}
              </span>
            )}
            {roles !== '' && (
              <span className="w-full max-w-[9rem] truncate text-xs text-fg-muted xl:max-w-[14rem]" title={roles}>
                {roles}
              </span>
            )}
          </span>
          <ChevronDownIcon className="hidden h-4 w-4 shrink-0 text-fg-muted min-[480px]:block" />
        </span>
      }
    >
      {/* Menu header: not a menuitem — it is the identity the actions below apply to, and at
          390px it is the only place the full name and role are legible. */}
      <div className="flex flex-col gap-0.5 border-b border-border px-2 pb-2 pt-1">
        {name !== null && <span className="break-words text-sm font-medium text-fg">{name}</span>}
        {roles !== '' && <span className="break-words text-xs text-fg-muted">{roles}</span>}
      </div>
      <DropdownItem onSelect={() => void signOut()}>
        <LogoutIcon className="me-2 h-4 w-4 shrink-0" />
        გასვლა
      </DropdownItem>
    </Dropdown>
  );
}
